export interface TableTotals {
    rows: number
    sum: number
    // Only set while a column filter is on.
    filtered?: {
        rows: number
        sum: number
    }
}

// Singular and plural, e.g. ['segment', 'segments'].
export type CountLabel = [string, string]

export interface TableTotalsLabels {
    rows: CountLabel
    sum: CountLabel
}

export const getTableTotals = <T>(
    rows: readonly T[],
    filteredRows: readonly T[] | undefined,
    getValue: (row: T) => number,
): TableTotals => {
    const sum = (list: readonly T[]) =>
        list.reduce((total, row) => total + getValue(row), 0)

    return {
        rows: rows.length,
        sum: sum(rows),
        ...(filteredRows && {
            filtered: {
                rows: filteredRows.length,
                sum: sum(filteredRows),
            },
        }),
    }
}

const numberFormat = new Intl.NumberFormat('en-US')

const formatCount = (
    total: number,
    [one, other]: CountLabel,
    filtered?: number,
) => {
    const label = total === 1 ? one : other
    const value = numberFormat.format(total)

    if (filtered === undefined) {
        return `${value} ${label}`
    }

    return `${numberFormat.format(filtered)} of ${value} ${label}`
}

// `available` is how many rows exist upstream; when it is more than were
// loaded, the line says so instead of passing the loaded count off as the
// total.
export const formatTableTotals = (
    totals: TableTotals,
    labels: TableTotalsLabels,
    available?: number,
) => {
    const parts = [
        formatCount(totals.rows, labels.rows, totals.filtered?.rows),
        formatCount(totals.sum, labels.sum, totals.filtered?.sum),
    ]

    if (available !== undefined && available > totals.rows) {
        parts.push(
            `only ${numberFormat.format(totals.rows)} of ${numberFormat.format(available)} loaded`,
        )
    }

    return parts.join(' · ')
}
